// /brokers/funding — broker funding and payout review (SM-716, SM-621
// phase 08a; the reads of SM-697 and SM-710).
//
// Pick a node, then review its money one currency per row: a root's
// MASTER or any other node's POOL (available, reserved, pending
// withdrawal — ledger reads made during the call) and every funding
// transfer it is party to, with kind, state, parties, amount, who asked
// and the operation id. Nothing on this page moves money: a root
// master's top-up and payout stay on the Treasury system-account page
// (manual mint / burn or the chain rail), and payout batches have their
// own review queue — both linked from here. Reads need BROKER_VIEW.
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";

import { BrokerFundingPanel } from "@/components/brokers/BrokerFundingPanel";
import { BrokerPicker } from "@/components/brokers/BrokerPicker";
import { EmptyState } from "@/components/ui/EmptyState";
import { Skeleton } from "@/components/ui/Skeleton";
import { useMyPerms } from "@/hooks/useMyPerms";
import { Perm } from "@/lib/perms";

interface Search {
  broker?: string;
}

export const Route = createFileRoute("/_app/brokers/funding")({
  validateSearch: (s: Record<string, unknown>): Search => ({
    broker: typeof s.broker === "string" && s.broker.trim() ? s.broker.trim() : undefined,
  }),
  component: BrokerFundingReviewPage,
});

function BrokerFundingReviewPage() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const perms = useMyPerms();

  if (perms.loaded && !perms.has(Perm.BrokerView)) {
    return <EmptyState title="No access" description="You need the BROKER_VIEW permission to review broker funding." />;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl tracking-tight">Funding review</h1>
        <p className="text-sm text-muted">
          Each broker&apos;s funding accounts per currency and the transfers that moved them. Review only: master
          top-ups and payouts are run on Treasury, and payout batches are approved in their own queue.
        </p>
        <div className="mt-2 flex flex-wrap gap-4 text-2xs">
          {perms.has(Perm.BrokerRevenueView) ? (
            <Link to="/brokers/payouts" className="text-neon hover:underline">
              Payout batch review
            </Link>
          ) : null}
          {perms.has(Perm.SystemAccountView) ? (
            <Link to="/treasury/accounts" className="text-neon hover:underline">
              Treasury system accounts
            </Link>
          ) : null}
          <Link to="/brokers/reconciliation" className="text-neon hover:underline">
            Funding reconciliation
          </Link>
        </div>
      </div>

      {/* Nothing is asked for until the permissions say it may be read. */}
      {perms.loaded ? (
        <>
          <BrokerPicker value={search.broker} onChange={(id) => void navigate({ search: { broker: id } })} />
          {search.broker ? <BrokerFundingPanel brokerId={search.broker} /> : null}
        </>
      ) : (
        <Skeleton className="h-24" />
      )}
    </div>
  );
}
